import { canIndexChecklists, canIndexSchedules, canViewInspections } from '@/ui/helpers';
import { computed, h, defineComponent } from 'vue';
import { level, pageTitle } from '@/ui/components';
import { ChecklistIndexTab } from '@/ui/pages/forms/FormsIndexPage/tabs/ChecklistIndexTab';
import { hasPermission, Permissions } from '@/ui/auth/auth';
import { ChecklistCreateButton } from '@/ui/pages/checklist/ChecklistCreateButton';
import { t } from '@/ui/i18n/i18n';
import { InspectionPendingTab } from './tabs/InspectionPendingTab';
import { UpcomingFormsTab } from './tabs/UpcomingFormsTab';
import { InspectionDoneTab } from './tabs/InspectionDoneTab';
import { InspectionNegativeAnswerView } from './tabs/inspectionNegativeAnswer/InspectionNegativeAnswerView';
import AiPhotoValidationTab from './tabs/AiPhotoValidationTab.vue';
import * as qs from '@/ui/queryState';
import { secondaryNavbar, TabItem } from '@/ui/components/navigation/SecondaryNavbar';
import { FormsTabNames, queryTabName } from '@/ui/components/navigation/constants';
import { InspectionDraftTab } from '@/ui/pages/forms/FormsIndexPage/tabs/InspectionDraftTab';
import { isAiImageValidationEnabled, isAiTemplateEnabled } from '@/ui/helpers/featureHelper';
import ScheduleCreateButton from '@/ui/pages/scheduleManager/ScheduleCreateButton.vue';
import ScheduleIndexTab from '@/ui/pages/forms/FormsIndexPage/tabs/ScheduleIndexTab.vue';

export const FormsPage = defineComponent({
  name: 'FormsPage',
  setup() {
    const shouldShowCreateChecklistButton = computed<boolean>(() => (
      [FormsTabNames.TEMPLATES, FormsTabNames.OVERDUE, FormsTabNames.DUE, FormsTabNames.UPCOMING]
        .includes(activeTab.value)
      && hasPermission(Permissions.MANAGE_CHECKLISTS)
    ));

    const shouldShowCreateScheduleButton = computed<boolean>(() => (
      activeTab.value === FormsTabNames.SCHEDULES
      && hasPermission(Permissions.MANAGE_CHECKLISTS)
    ));

    const activeTab = qs.stringEnum<FormsTabNames>(
      queryTabName,
      Object.values(FormsTabNames),
      canIndexChecklists.value ? FormsTabNames.TEMPLATES : FormsTabNames.OVERDUE,
      false,
    );

    let secondaryTabItems : TabItem[] = [];

    if (canIndexChecklists.value) {
      secondaryTabItems.push({
        value: FormsTabNames.TEMPLATES,
        label: t('common.templates'),
        icon: 'file-alt',
        tooltipLabel: t('feature-explanation.templates'),
        underLineColor: 'blue',
      });
    }

    if (canIndexSchedules.value) {
      secondaryTabItems.push({
        value: FormsTabNames.SCHEDULES,
        label: t('inspections.schedules'),
        icon: 'calendar-plus',
        tooltipLabel: t('feature-explanation.schedules-tab'),
        underLineColor: 'red',
      });
    }

    secondaryTabItems = secondaryTabItems.concat([
      {
        label: t('common.scheduled'),
        icon: 'hourglass-half',
        iconPack: 'fal',
        tooltipLabel: t('feature-explanation.pending-inspections-tab'),
        options: [{
          value: FormsTabNames.OVERDUE,
          label: t('common.overdue'),
          icon: 'file-exclamation',
          underLineColor: 'red',
        }, {
          value: FormsTabNames.DUE,
          label: t('common.due'),
          icon: 'alarm-clock',
          underLineColor: 'orange',
        }, {
          value: FormsTabNames.UPCOMING,
          label: t('common.upcoming'),
          icon: 'arrow-square-up',
          underLineColor: 'blue',
        }],
      },
      {
        value: FormsTabNames.SUBMITTED,
        label: t('common.submitted'),
        icon: 'trophy-alt',
        tooltipLabel: t('feature-explanation.done-inspections-tab'),
        underLineColor: 'green',
      },
      {
        value: FormsTabNames.DRAFT,
        label: t('common.drafts'),
        icon: 'file-edit',
        tooltipLabel: t('feature-explanation.draft-inspections-tab'),
        underLineColor: 'orange',
      },
      {
        value: FormsTabNames.NEGATIVE_RESPONSES,
        label: t('inspections.negative-answers'),
        icon: 'minus-circle',
        tooltipLabel: t('feature-explanation.negative-answers'),
        underLineColor: 'red',
      },
    ]);

    if (canViewInspections.value) {
      secondaryTabItems.push({
        value: FormsTabNames.AI_PHOTO_VALIDATION,
        label: t('ai-photo-validation.tab-title'),
        icon: 'image',
        tooltipLabel: t('feature-explanation.ai-photo-validation'),
        underLineColor: 'blue',
        hide: !isAiImageValidationEnabled(),
        isNew: true,
      });
    }

    const getTab = () => {
      switch (activeTab.value) {
        case FormsTabNames.TEMPLATES:
          return ChecklistIndexTab;
        case FormsTabNames.OVERDUE:
        case FormsTabNames.DUE:
          return InspectionPendingTab;
        case FormsTabNames.UPCOMING:
          return UpcomingFormsTab;
        case FormsTabNames.SUBMITTED:
          return InspectionDoneTab;
        case FormsTabNames.DRAFT:
          return InspectionDraftTab;
        case FormsTabNames.NEGATIVE_RESPONSES:
          return InspectionNegativeAnswerView;
        case FormsTabNames.AI_PHOTO_VALIDATION:
          return AiPhotoValidationTab;
        case FormsTabNames.SCHEDULES:
          return ScheduleIndexTab;
        default:
          throw new Error('Invalid tab selected. Please use FormsTabsEnum enumeration');
      }
    };

    const renderCreateButton = () => {
      if (shouldShowCreateChecklistButton.value) {
        return h(ChecklistCreateButton, {
          props: {
            text: t('checklist.create-template'),
            trackClick: true,
            query: isAiTemplateEnabled() ? { fromRoute: 'forms' } : {},
          },
        });
      }

      if (shouldShowCreateScheduleButton.value) {
        return h(ScheduleCreateButton);
      }

      return null;
    };

    return function render() {
      return h('div', [
        level(
          [
            pageTitle(t('common.checklists')),
          ],
          [
            renderCreateButton(),
          ],
        ),
        secondaryNavbar({
          props: {
            value: activeTab.value,
            tabItems: secondaryTabItems,
          },
          on: {
            input: (value: FormsTabNames) => {
              activeTab.value = value;
            },
          },
        }),
        h(getTab(), {
          props: {
            activeTab: activeTab.value,
          },
        }),
      ]);
    };
  },
});
